// Plan 24c, Task 3 — Minigame Instructions screen (Screen 20)
//
// T3 layout: dimmed overlay + centered panel showing minigame name, objective,
// keyboard/gamepad control diagrams, and a GET READY button.
// Auto-starts navigation to 'minigame-countdown' after autoStartMs (default 5000ms).
// ESC key → return to warmup.
//
// Design system references:
//   - docs/design/tokens.json   — all visual values
//   - docs/design/components.md §24 — Minigame Instructions Panel
//   - docs/design/screens.md Screen 20 — T3 template, Minigame group

import { createButton } from '../components/button';
import { createPanel } from '../components/panel';
import type { ScreenHandle, ScreenMountContext } from './types';
import type { BoardMountPayload } from './board-payload';
import { getInputModality } from '../../input/modality';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export type MinigameControl = {
  glyph: string; // e.g. '↑', 'SPACE', 'A', 'STICK'
  label?: string; // optional sub-caption under the icon
};

export type MinigameCategoryColor = 'action' | 'puzzle' | 'collection';

export type MinigameInstructionsPayload = BoardMountPayload & {
  minigameName: string;
  objective: string;
  categoryColor: MinigameCategoryColor; // → action=color.button.danger.base, puzzle=color.button.secondary.base, collection=color.text.success
  keyboardControls: MinigameControl[]; // ≥1 entry
  gamepadControls: MinigameControl[]; // ≥1 entry
  touchControls?: MinigameControl[];
  autoStartMs?: number; // default 5000ms
  onReady?: () => void; // default: router.navigate('minigame-countdown', payload)
};

// ─────────────────────────────────────────────────────────────────────────────
// Design token constants — all values from docs/design/tokens.json
// ─────────────────────────────────────────────────────────────────────────────

/** z-index.overlay = 350 */
const Z_INDEX_OVERLAY = 350;

/** color.ui.overlay-dim = rgba(0,0,0,0.60) */
const OVERLAY_BG = 'rgba(0, 0, 0, 0.60)';

/** layout.panel-max-width = 560px */
const PANEL_MAX_WIDTH = '560px';

/** typography.family.headline */
const HEADLINE_FAMILY = "'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif";

/** typography.size.h1 = 48px */
const SIZE_H1 = '48px';

/** typography.size.h5 = 18px */
const SIZE_H5 = '18px';

/** typography.size.body = 16px */
const SIZE_BODY = '16px';

/** typography.size.caption = 14px */
const SIZE_CAPTION = '14px';

/** color.text.primary = #FFFFFF */
const COLOR_WHITE = '#FFFFFF';

/** color.text.secondary = #B0B8C8 */
const COLOR_TEXT_SECONDARY = '#B0B8C8';

/** color.text.muted = #707888 */
const COLOR_TEXT_MUTED = '#707888';

/** color.ui.panel-bg = #1E2332 */
const COLOR_PANEL_BG = '#1E2332';

/** border-radius.sm = 6px */
const BORDER_RADIUS_SM = '6px';

/** typography.shadow.heading — for text on dark backgrounds */
const TEXT_SHADOW_HEADING = '0 2px 0 #000000, 0 4px 8px rgba(0,0,0,0.60)';

/** Category color mapping from tokens.json */
const CATEGORY_COLORS: Record<MinigameCategoryColor, string> = {
  action: '#D94040', // color.button.danger.base
  puzzle: '#4A8ED9', // color.button.secondary.base
  collection: '#5CAD40', // color.text.success
};

// ─────────────────────────────────────────────────────────────────────────────
// Helper — build one column of controls (KEYBOARD or GAMEPAD)
// ─────────────────────────────────────────────────────────────────────────────

function buildControlColumn(
  colId: 'keyboard' | 'gamepad',
  headerText: string,
  controls: MinigameControl[],
): HTMLElement {
  const col = document.createElement('div');
  col.setAttribute('data-control-col', colId);
  col.style.cssText = [
    'flex: 1',
    'display: flex',
    'flex-direction: column',
    'align-items: center',
    'gap: 8px',
  ].join('; ');

  // Column header (KEYBOARD / GAMEPAD)
  const header = document.createElement('div');
  header.textContent = headerText;
  header.style.cssText = [
    `font-family: ${HEADLINE_FAMILY}`,
    `font-size: ${SIZE_H5}`,
    `color: ${COLOR_TEXT_MUTED}`,
    'text-transform: uppercase',
    'letter-spacing: 2px',
    'font-weight: 700',
    'margin-bottom: 4px',
  ].join('; ');
  col.appendChild(header);

  // Icon entries
  for (const control of controls) {
    const iconWrap = document.createElement('div');
    iconWrap.style.cssText = [
      'display: flex',
      'flex-direction: column',
      'align-items: center',
      'gap: 2px',
    ].join('; ');

    const icon = document.createElement('div');
    icon.setAttribute('data-control-icon', colId);
    icon.textContent = control.glyph;
    icon.style.cssText = [
      'width: 36px',
      'height: 36px',
      `background: ${COLOR_PANEL_BG}`,
      `border-radius: ${BORDER_RADIUS_SM}`,
      'display: flex',
      'align-items: center',
      'justify-content: center',
      `color: ${COLOR_WHITE}`,
      `font-family: ${HEADLINE_FAMILY}`,
      `font-size: ${SIZE_H5}`,
      'font-weight: 700',
    ].join('; ');
    iconWrap.appendChild(icon);

    if (control.label) {
      const sublabel = document.createElement('div');
      sublabel.textContent = control.label;
      sublabel.style.cssText = [
        `font-size: ${SIZE_CAPTION}`,
        `color: ${COLOR_TEXT_MUTED}`,
        'text-align: center',
      ].join('; ');
      iconWrap.appendChild(sublabel);
    }

    col.appendChild(iconWrap);
  }

  return col;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createMinigameInstructionsScreen(
  ctx: ScreenMountContext<MinigameInstructionsPayload>,
): ScreenHandle {
  const { gsapRegistry, host, router, payload } = ctx;
  const {
    minigameName,
    objective,
    categoryColor,
    keyboardControls,
    gamepadControls,
    touchControls,
    autoStartMs,
    onReady,
  } = payload;

  let disposed = false;

  const nameColor = CATEGORY_COLORS[categoryColor];

  // ── Root overlay (T3: dimmed background) ─────────────────────────────────

  const root = document.createElement('div');
  root.className = 'screen-minigame-instructions';
  root.setAttribute('data-screen-id', 'minigame-instructions');
  root.style.cssText = [
    'position: fixed',
    'inset: 0',
    `z-index: ${Z_INDEX_OVERLAY}`,
    `background: ${OVERLAY_BG}`,
    'display: flex',
    'align-items: center',
    'justify-content: center',
  ].join('; ');

  // ── Panel mount point ─────────────────────────────────────────────────────

  const panelMount = document.createElement('div');
  panelMount.style.cssText = [`max-width: ${PANEL_MAX_WIDTH}`, 'width: 100%'].join('; ');
  root.appendChild(panelMount);

  // ── Create panel ──────────────────────────────────────────────────────────

  const panelHandle = createPanel({ gsapRegistry, mount: panelMount, padding: 'lg' });
  panelHandle.show();

  // ── Build panel content ───────────────────────────────────────────────────

  const contentEl = document.createElement('div');
  contentEl.style.cssText = ['display: flex', 'flex-direction: column', 'gap: 16px'].join('; ');

  // Minigame name (h1, category color)
  const nameEl = document.createElement('h1');
  nameEl.textContent = minigameName;
  nameEl.style.cssText = [
    `font-family: ${HEADLINE_FAMILY}`,
    `font-size: ${SIZE_H1}`,
    `color: ${nameColor}`,
    `text-shadow: ${TEXT_SHADOW_HEADING}`,
    'margin: 0',
    'font-weight: 700',
  ].join('; ');
  contentEl.appendChild(nameEl);

  // Objective section
  const objectiveSection = document.createElement('div');

  const objectiveLabel = document.createElement('h5');
  objectiveLabel.textContent = 'Objective:';
  objectiveLabel.style.cssText = [
    `font-family: ${HEADLINE_FAMILY}`,
    `font-size: ${SIZE_H5}`,
    `color: ${COLOR_WHITE}`,
    'margin: 0 0 4px',
    'font-weight: 700',
  ].join('; ');
  objectiveSection.appendChild(objectiveLabel);

  const objectiveText = document.createElement('p');
  objectiveText.textContent = objective;
  objectiveText.style.cssText = [
    `font-size: ${SIZE_BODY}`,
    `color: ${COLOR_TEXT_SECONDARY}`,
    'margin: 0',
  ].join('; ');
  objectiveSection.appendChild(objectiveText);

  contentEl.appendChild(objectiveSection);

  // Controls grid: gamepad only when connected; otherwise desktop or touch controls.
  const controlsGrid = document.createElement('div');
  controlsGrid.style.cssText = ['display: flex', 'gap: 24px'].join('; ');
  const modality = getInputModality();
  if (modality === 'gamepad') {
    controlsGrid.appendChild(buildControlColumn('gamepad', 'GAMEPAD', gamepadControls));
  } else if (modality === 'touch') {
    controlsGrid.appendChild(
      buildControlColumn('keyboard', 'TOUCH', touchControls ?? [{ glyph: 'TAP', label: 'Action' }]),
    );
  } else {
    controlsGrid.appendChild(buildControlColumn('keyboard', 'KEYBOARD', keyboardControls));
  }
  contentEl.appendChild(controlsGrid);

  // GET READY button — mounted into a wrapper div, then appended to content
  const btnMount = document.createElement('div');
  btnMount.style.cssText = ['display: flex', 'justify-content: center', 'margin-top: 8px'].join(
    '; ',
  );

  const btnHandle = createButton({
    gsapRegistry,
    mount: btnMount,
    variant: 'danger',
    size: 'large',
    label: 'GET READY!',
    onClick: () => {
      if (disposed) return;
      if (onReady) {
        onReady();
      } else {
        router.navigate('minigame-countdown', payload);
      }
    },
  });

  contentEl.appendChild(btnMount);

  // Set content on panel
  panelHandle.setContent(contentEl);

  // ── Auto-start timer ──────────────────────────────────────────────────────

  const autoHandle = gsapRegistry.create('minigame-instructions:auto');
  const autoSeconds = (autoStartMs ?? 5000) / 1000;

  autoHandle.timeline.call(
    () => {
      if (disposed) return;
      if (onReady) {
        onReady();
      } else {
        router.navigate('minigame-countdown', payload);
      }
    },
    undefined,
    autoSeconds,
  );

  // ── ESC key handler ───────────────────────────────────────────────────────

  function handleEsc(e: KeyboardEvent): void {
    if (e.key !== 'Escape') return;
    router.navigate('warmup-arena-overlay', { portal: null });
  }

  document.addEventListener('keydown', handleEsc);

  // ── Append root to host ───────────────────────────────────────────────────

  host.appendChild(root);

  // ── dispose ───────────────────────────────────────────────────────────────

  return {
    element: root,
    dispose() {
      if (disposed) return;
      disposed = true;

      autoHandle.dispose();
      panelHandle.dispose();
      btnHandle.dispose();
      document.removeEventListener('keydown', handleEsc);
      if (root.parentNode) root.parentNode.removeChild(root);
    },
  };
}
